import React from 'react';
import { EventClickArg, EventDropArg } from '@fullcalendar/core';
import type { PendingEventReschedule } from '../calendar-event-types';
import type { UpdateServiceProviderEventValues } from '../dialogs/event-dialog-types';
import type { RecurrencePattern } from '../../../../../components/calendar/recurrence/recurrence-pattern';
import {
  mapDropToPendingReschedule,
  mapResizeToPendingReschedule,
} from '../calendar-event-interaction-mappers';

interface UpdateEventValues {
  eventId: string;
  title: string;
  locationAddress: string;
  startAt: Date | string;
  endAt: Date | string;
  allDay: boolean;
  content?: string;
  responsibleUserId: string;
  consumedMinutes?: number;
  recurrence?: RecurrencePattern;
}

interface ResizeEventInfo {
  event: EventClickArg['event'];
  oldEvent: EventClickArg['event'];
  revert: () => void;
}

interface UsePendingRescheduleOptions {
  updateEvent: (values: UpdateEventValues) => Promise<void>;
  /** Required to support per-occurrence apply when the drag/resize targets a recurring series. */
  updateOccurrence?: (values: UpdateServiceProviderEventValues, occurrenceStartAt: Date) => Promise<void>;
}

export function usePendingReschedule(options: UsePendingRescheduleOptions) {
  const { updateEvent, updateOccurrence } = options;

  const [pendingReschedule, setPendingReschedule] =
    React.useState<PendingEventReschedule | null>(null);
  const [rescheduleError, setRescheduleError] = React.useState<string | null>(null);
  const [isRescheduleSubmitting, setIsRescheduleSubmitting] = React.useState(false);

  function handleEventDrop(dropInfo: EventDropArg) {
    const pending = mapDropToPendingReschedule(dropInfo);
    if (!pending) {
      dropInfo.revert();
      return;
    }

    setRescheduleError(null);
    setPendingReschedule(pending);
  }

  function handleEventResize(resizeInfo: ResizeEventInfo) {
    const pending = mapResizeToPendingReschedule(resizeInfo);
    if (!pending) {
      resizeInfo.revert();
      return;
    }

    setRescheduleError(null);
    setPendingReschedule(pending);
  }

  function handleCancelReschedule() {
    if (!pendingReschedule || isRescheduleSubmitting) {
      return;
    }

    pendingReschedule.revert();
    setPendingReschedule(null);
    setRescheduleError(null);
  }

  async function handleConfirmReschedule() {
    if (!pendingReschedule) {
      return;
    }

    setIsRescheduleSubmitting(true);
    setRescheduleError(null);
    try {
      await updateEvent({
        eventId: pendingReschedule.eventId,
        title: pendingReschedule.title,
        locationAddress: pendingReschedule.locationAddress,
        startAt: pendingReschedule.newStartAt.toISOString(),
        endAt: pendingReschedule.newEndAt.toISOString(),
        allDay: pendingReschedule.allDay,
        content: pendingReschedule.content,
        responsibleUserId: pendingReschedule.responsibleUserId,
        consumedMinutes: pendingReschedule.consumedMinutes,
      });
      setPendingReschedule(null);
    } catch {
      pendingReschedule.revert();
      setRescheduleError('Kunne ikke gemme ændringen af begivenheden. Prøv igen.');
      setPendingReschedule(null);
    } finally {
      setIsRescheduleSubmitting(false);
    }
  }

  // Detach only this dragged/resized occurrence from the series with its new times.
  async function applyToOccurrence() {
    if (!pendingReschedule?.isSeriesOccurrence || !pendingReschedule.seriesId || !pendingReschedule.occurrenceStartAt) {
      return;
    }

    if (!updateOccurrence) {
      return;
    }

    setIsRescheduleSubmitting(true);
    setRescheduleError(null);
    try {
      await updateOccurrence(
        {
          eventId: pendingReschedule.seriesId,
          title: pendingReschedule.title,
          locationAddress: pendingReschedule.locationAddress,
          startAt: pendingReschedule.newStartAt.toISOString(),
          endAt: pendingReschedule.newEndAt.toISOString(),
          allDay: pendingReschedule.allDay,
          content: pendingReschedule.content,
          responsibleUserId: pendingReschedule.responsibleUserId,
          consumedMinutes: pendingReschedule.consumedMinutes,
        },
        pendingReschedule.occurrenceStartAt
      );
      setPendingReschedule(null);
    } catch {
      pendingReschedule.revert();
      setRescheduleError('Kunne ikke gemme ændringen af begivenheden. Prøv igen.');
      setPendingReschedule(null);
    } finally {
      setIsRescheduleSubmitting(false);
    }
  }

  // Apply the drag/resize delta to the whole series by shifting the master anchor.
  async function applyToSeries() {
    if (!pendingReschedule?.isSeriesOccurrence || !pendingReschedule.seriesId) {
      return;
    }

    if (!pendingReschedule.masterStartAt || !pendingReschedule.masterEndAt || !pendingReschedule.occurrenceStartAt) {
      return;
    }

    setIsRescheduleSubmitting(true);
    setRescheduleError(null);
    try {
      let newMasterStartAt: Date;
      let newMasterEndAt: Date;

      if (pendingReschedule.actionType === 'move') {
        // Shift the master anchor by the same delta the user dragged this occurrence.
        const deltaMs = pendingReschedule.newStartAt.getTime() - pendingReschedule.occurrenceStartAt.getTime();
        newMasterStartAt = new Date(pendingReschedule.masterStartAt.getTime() + deltaMs);
        newMasterEndAt = new Date(pendingReschedule.masterEndAt.getTime() + deltaMs);
      } else {
        // Resize: only the end changes; the master's start anchor stays.
        const endDeltaMs = pendingReschedule.newEndAt.getTime() - pendingReschedule.originalEndAt.getTime();
        newMasterStartAt = pendingReschedule.masterStartAt;
        newMasterEndAt = new Date(pendingReschedule.masterEndAt.getTime() + endDeltaMs);
      }

      await updateEvent({
        eventId: pendingReschedule.seriesId,
        title: pendingReschedule.title,
        locationAddress: pendingReschedule.locationAddress,
        startAt: newMasterStartAt.toISOString(),
        endAt: newMasterEndAt.toISOString(),
        allDay: pendingReschedule.allDay,
        content: pendingReschedule.content,
        responsibleUserId: pendingReschedule.responsibleUserId,
        consumedMinutes: pendingReschedule.consumedMinutes,
        // Preserving the recurrence pattern keeps the event recurring after the update.
        recurrence: pendingReschedule.recurrence,
      });
      setPendingReschedule(null);
    } catch {
      pendingReschedule.revert();
      setRescheduleError('Kunne ikke gemme ændringen af serien. Prøv igen.');
      setPendingReschedule(null);
    } finally {
      setIsRescheduleSubmitting(false);
    }
  }

  return {
    pendingReschedule,
    rescheduleError,
    isRescheduleSubmitting,
    handleEventDrop,
    handleEventResize,
    handleCancelReschedule,
    handleConfirmReschedule,
    applyToOccurrence,
    applyToSeries,
  };
}
